import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

const EmailEditor = forwardRef(({ content, onChange }, ref) => {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    insertImage: (imageData, attachmentIndex) => {
      const imgTag = `<img src="${imageData}" data-attachment-index="${attachmentIndex}" style="max-width: 100%; height: auto;" alt="Inline image">`;
      document.execCommand('insertHTML', false, imgTag);
      handleChange();
    },
    focus: () => {
      editorRef.current?.focus();
    }
  }));

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleChange();
  };

  const handleChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt('Digite a URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };

  const changeFont = (e) => {
    executeCommand('fontName', e.target.value);
  };

  const changeFontSize = (e) => {
    executeCommand('fontSize', e.target.value);
  };

  const changeColor = (e) => {
    executeCommand('foreColor', e.target.value);
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imgTag = `<img src="${event.target.result}" style="max-width: 100%; height: auto;" alt="Image">`;
        document.execCommand('insertHTML', false, imgTag);
        handleChange();
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    e.target.value = '';
  };

  const insertTable = () => {
    const rows = prompt('Número de linhas:', '3');
    const cols = prompt('Número de colunas:', '3');
    
    if (rows && cols) {
      let table = '<table border="1" style="border-collapse: collapse; width: 100%;">';
      for (let i = 0; i < parseInt(rows); i++) {
        table += '<tr>';
        for (let j = 0; j < parseInt(cols); j++) {
          table += '<td style="padding: 8px; border: 1px solid #ddd;">&nbsp;</td>';
        }
        table += '</tr>';
      }
      table += '</table><br>';
      
      document.execCommand('insertHTML', false, table);
      handleChange();
    }
  };

  return (
    <div className="email-editor">
      <div className="editor-toolbar" ref={toolbarRef}>
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => executeCommand('undo')}
            title="Desfazer"
            className="toolbar-btn"
          >
            <i className="fas fa-undo"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('redo')}
            title="Refazer"
            className="toolbar-btn"
          >
            <i className="fas fa-redo"></i>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <select onChange={changeFont} className="toolbar-select">
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Comic Sans MS">Comic Sans MS</option>
          </select>

          <select onChange={changeFontSize} className="toolbar-select">
            <option value="1">Muito pequeno</option>
            <option value="2">Pequeno</option>
            <option value="3" selected>Normal</option>
            <option value="4">Grande</option>
            <option value="5">Muito grande</option>
            <option value="6">Enorme</option>
          </select>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => executeCommand('bold')}
            title="Negrito"
            className="toolbar-btn"
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('italic')}
            title="Itálico"
            className="toolbar-btn"
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('underline')}
            title="Sublinhado"
            className="toolbar-btn"
          >
            <i className="fas fa-underline"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('strikeThrough')}
            title="Tachado"
            className="toolbar-btn"
          >
            <i className="fas fa-strikethrough"></i>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <input
            type="color"
            onChange={changeColor}
            title="Cor do texto"
            className="color-picker"
          />
          <button
            type="button"
            onClick={() => executeCommand('removeFormat')}
            title="Remover formatação"
            className="toolbar-btn"
          >
            <i className="fas fa-eraser"></i>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => executeCommand('justifyLeft')}
            title="Alinhar à esquerda"
            className="toolbar-btn"
          >
            <i className="fas fa-align-left"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('justifyCenter')}
            title="Centralizar"
            className="toolbar-btn"
          >
            <i className="fas fa-align-center"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('justifyRight')}
            title="Alinhar à direita"
            className="toolbar-btn"
          >
            <i className="fas fa-align-right"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('justifyFull')}
            title="Justificar"
            className="toolbar-btn"
          >
            <i className="fas fa-align-justify"></i>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => executeCommand('insertOrderedList')}
            title="Lista numerada"
            className="toolbar-btn"
          >
            <i className="fas fa-list-ol"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('insertUnorderedList')}
            title="Lista com marcadores"
            className="toolbar-btn"
          >
            <i className="fas fa-list-ul"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('indent')}
            title="Aumentar recuo"
            className="toolbar-btn"
          >
            <i className="fas fa-indent"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('outdent')}
            title="Diminuir recuo"
            className="toolbar-btn"
          >
            <i className="fas fa-outdent"></i>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={insertLink}
            title="Inserir link"
            className="toolbar-btn"
          >
            <i className="fas fa-link"></i>
          </button>
          <button
            type="button"
            onClick={insertImage}
            title="Inserir imagem"
            className="toolbar-btn"
          >
            <i className="fas fa-image"></i>
          </button>
          <button
            type="button"
            onClick={insertTable}
            title="Inserir tabela"
            className="toolbar-btn"
          >
            <i className="fas fa-table"></i>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('insertHorizontalRule')}
            title="Inserir linha horizontal"
            className="toolbar-btn"
          >
            <i className="fas fa-minus"></i>
          </button>
        </div>
      </div>

      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        onInput={handleChange}
        onBlur={handleChange}
        suppressContentEditableWarning={true}
      />
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageFile}
        style={{ display: 'none' }}
      />
    </div>
  );
});

export default EmailEditor;